/**
 * Configuración → Tipos de detracción (`/configuracion/tipos-detraccion`).
 *
 * Puerto de `Tipo_detraccion` + `views/tipo_detraccion/index.php`: las
 * operaciones sujetas a detracción con su porcentaje (001 Azúcar y melaza 10 %,
 * 020 Mantenimiento 12 %, 037 Demás servicios gravados 12 %…).
 *
 * Diferencias anotadas: el código no puede repetirse (409, la tabla no tiene
 * índice único), el porcentaje se valida entre 0 y 100 también en la API (el
 * formulario ya lo limitaba) y no hay borrado: se desactiva.
 */

import { ArrowLeft, Pencil, Percent, Plus, Power, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMutarTiposDetraccion, useTiposDetraccion } from '../catalogos.api';
import type { TipoDetraccion } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Alta/edición de un tipo de detracción. */
function TipoDialogo({
  tipo,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  tipo: TipoDetraccion | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (
    payload: { codigo: string; descripcion: string; porcentaje: number },
    activo?: boolean,
  ) => void;
  onCerrar: () => void;
}) {
  const [codigo, setCodigo] = useState(tipo?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(tipo?.descripcion ?? '');
  const [porcentaje, setPorcentaje] = useState(tipo?.porcentaje ?? 12);
  const [activo, setActivo] = useState(tipo?.activo ?? true);

  const valido =
    codigo.trim().length > 0 &&
    descripcion.trim().length > 0 &&
    Number.isFinite(porcentaje) &&
    porcentaje >= 0 &&
    porcentaje <= 100;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={tipo ? `Editar tipo ${tipo.codigo}` : 'Nuevo tipo de detracción'}
      description="Códigos y porcentajes del régimen de detracciones del IGV."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar(
                { codigo: codigo.trim(), descripcion: descripcion.trim(), porcentaje },
                tipo ? activo : undefined,
              )
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          <Input
            label="Código"
            value={codigo}
            maxLength={20}
            autoFocus={!tipo}
            placeholder="Ej. 001"
            onChange={(evento) => setCodigo(evento.target.value)}
          />
          <Input
            label="Porcentaje"
            type="number"
            min={0}
            max={100}
            step={1}
            value={String(porcentaje)}
            rightSlot={<span className="px-3 text-xs text-ink-400">%</span>}
            onChange={(evento) => {
              const bruto = evento.target.value;
              setPorcentaje(bruto === '' ? 0 : Number(bruto));
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="tipo-detraccion-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="tipo-detraccion-descripcion"
            className="min-h-20 w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            value={descripcion}
            placeholder="Ej. Mantenimiento y reparación de bienes muebles"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>

        {tipo ? (
          <Checkbox
            label="Tipo activo"
            hint="Sólo los activos se ofrecen al registrar una operación con detracción"
            checked={activo}
            onChange={(evento) => setActivo(evento.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

export function TiposDetraccionPage() {
  useDocumentTitle('Configuración · Tipos de detracción');

  const tipos = useTiposDetraccion();
  const mutaciones = useMutarTiposDetraccion();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; tipo: TipoDetraccion | null }>({
    abierto: false,
    tipo: null,
  });
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = tipos.error instanceof ApiError && tipos.error.status === 403;
  const lista = tipos.data?.tipos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Percent className="size-6 text-brand-500" aria-hidden="true" />
          Tipos de detracción
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Operaciones sujetas a detracción con su porcentaje de retención.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, tipo: null });
        }}
      >
        Nuevo tipo
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (
    payload: { codigo: string; descripcion: string; porcentaje: number },
    activo?: boolean,
  ) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, tipo: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) =>
        setErrorDialogo(mensajeDe(error, 'No se pudo guardar el tipo de detracción.')),
    };

    if (dialogo.tipo) {
      mutaciones.actualizar.mutate(
        { id: dialogo.tipo.id, payload: { ...payload, ...(activo === undefined ? {} : { activo }) } },
        alGuardar,
      );
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = (tipo: TipoDetraccion) => {
    mutaciones.cambiarEstado.mutate(
      { id: tipo.id, activo: !tipo.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {tipos.isLoading ? (
        <Skeleton className="h-80 w-full" />
      ) : tipos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los tipos de detracción">
          {mensajeDe(tipos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Catálogo de tipos de detracción</CardTitle>
            <Badge variant="neutral">{tipos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[44rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-24 pb-2">Código</th>
                    <th className="pb-2">Descripción</th>
                    <th className="w-28 pb-2 text-right">Porcentaje</th>
                    <th className="w-28 pb-2">Estado</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((tipo) => (
                    <tr key={tipo.id} className="border-t border-ink-100 align-top">
                      <td className="py-3">
                        <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                          {tipo.codigo}
                        </span>
                      </td>
                      <td className="py-3 text-ink-800">{tipo.descripcion}</td>
                      <td className="py-3 text-right font-medium text-ink-900">
                        {tipo.porcentaje} %
                      </td>
                      <td className="py-3">
                        <Badge variant={tipo.activo ? 'success' : 'neutral'}>
                          {tipo.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar tipo ${tipo.codigo}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, tipo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={
                              tipo.activo ? `Desactivar tipo ${tipo.codigo}` : `Activar tipo ${tipo.codigo}`
                            }
                            title={tipo.activo ? 'Desactivar' : 'Activar'}
                            loading={mutaciones.cambiarEstado.isPending}
                            onClick={() => cambiarEstado(tipo)}
                          >
                            <Power
                              className={`size-4 ${tipo.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <TipoDialogo
          key={`detraccion-${dialogo.tipo?.id ?? 'nuevo'}`}
          tipo={dialogo.tipo}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, tipo: null })}
        />
      ) : null}
    </div>
  );
}
